import axios from '@fs/zion-axios'
import { useEffect, useState, useMemo } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { getSessionId } from '@fs/zion-session'
import { useShowTemple } from '@fs/zion-permissions'
import { useUser } from '@fs/zion-user'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from '@fs/zion-router'
import { appPath } from '@fs/zion-config'
import { useQueryClient, useQueries } from '@tanstack/react-query'
import {
  getFamilyGroups,
  getFilters,
  getMemoryById,
  getCurrentTemple,
  getInvitationsReceived,
  getFamilyGroupsWithPoints,
  getPhotosForAlbum,
  getMyPoints,
  getActivityGroupAnswers,
  getActivities,
  saveAnswers,
  saveMemoryDescription,
  saveMemoryTitle,
  addImagesToMemory,
  removeImageFromMemory,
  updateMemoryDate,
  markAsComplete,
  setGrouping,
  deleteAnswerGroup,
  deletePhotoFromAlbum,
  savePhotoForAlbum,
  getCampaigns,
  getGroupMembers,
  markAnswerAsRead,
  getMemoryFromMemories,
  getActivityGroupAnswerCount,
  getTempleCountdownDismiss,
  getActivityGroupAnswerById,
  getStreaks,
  postPushNotificationRegistration,
  // newly used services
  getTempleEligible,
  getSharingPreferences,
  deleteSharingPreference,
  getConsolidatedAnswers,
  getActivityGroupImages,
} from '../services/services'
import { getGroup } from '../services/groupservices'
import useReactQuery from './useReactQuery'
import useReactQueryMutation from './useReactQueryMutation'
import topics from '../components/topics'
import { activityGroupAllowedRoutes } from '../helpers/routes'
import { updateAnswerInArray } from '../helpers/activities_helper'
import { qk } from './queryKeys'
import { invalidatePreviousAnswerGroups, invalidatePoints } from './mutationSideEffects'

const orchestrationBase = '/service/discovery/mystory/orchestration'

const useGetActivityGroupEnabledUrl = (url) => {
  const showInactiveActivityGroups = useFeatureFlag('discovery_familyConnect_showInactiveActivityGroups')
  let newUrl = url
  if (showInactiveActivityGroups.isOn) {
    const params = new URLSearchParams({
      activeOnly: false,
    })
    newUrl = `${url}?${params.toString()}`
  }
  return newUrl
}

export const useGetTempleDismissed = () => {
  return useReactQuery({
    queryFn: getTempleCountdownDismiss,
    staleTime: 86400000, // 1 day
    queryKey: qk.templeDismissed(),
    enabled: true,
  })
}

export const useCurrentTemple = ({ signedIn, loading }) => {
  const [showTemple] = useShowTemple()
  return useReactQuery({
    queryFn: getCurrentTemple,
    staleTime: 300000,
    queryKey: qk.assignedTemple(),
    enabled: signedIn && !loading && showTemple,
    hideErrorOverlay: true,
  })
}

export const useGetActivityGroupAnswerCount = () => {
  const [activityGroupAnswerCountData, setActivityGroupAnswerCountData] = useState([])
  const { data: countData } = useReactQuery({
    queryFn: getActivityGroupAnswerCount,
    staleTime: 0,
    queryKey: qk.activityGroupAnswerCount(),
  })

  const countsString = countData?.items
    ?.sort((a, b) => a.activityGroupId - b.activityGroupId) // sort so count join(',') strings are consistent
    .map((item) => item.count)
    .join(',')

  useEffect(() => {
    if (countData?.items?.length > 0) {
      // create new object references for when properties like 'count' change so we rerender
      const newCountItems = countData.items.map((item) => ({ ...item }))
      setActivityGroupAnswerCountData(newCountItems)
    }
  }, [countData?.items, countsString]) // rerender when items or item counts change

  return { activityGroupAnswerCountData }
}

export const useGetCategories = (topicId, enabled) => {
  const base = `${orchestrationBase}/topics`
  const url = useGetActivityGroupEnabledUrl(`${base}/${topicId}/categories`)

  const templeUrl = useGetActivityGroupEnabledUrl(`${base}/${topics.temple.id}/categories`)
  const storyUrl = useGetActivityGroupEnabledUrl(`${base}/${topics.story.id}/categories`)

  // In our useReactQuery hook, when the browser is idle, we'll preload the category we think is likely to be clicked on next
  const preloadCategoryQueries = {
    story: {
      queryKey: qk.categories(topics.story.id),
      queryFn: () => axios.get(storyUrl),
      enabled,
    },
    temple: {
      queryKey: qk.categories(topics.temple.id),
      queryFn: () => axios.get(templeUrl),
      enabled,
    },
  }

  let preloadQuery = preloadCategoryQueries.story

  if (topicId === topics.story.id) {
    preloadQuery = preloadCategoryQueries.temple
  }

  return useReactQuery({
    queryFn: () => axios.get(url),
    staleTime: 86400000, // 1 day
    queryKey: qk.categories(topicId),
    enabled,
    nextPrefetch: preloadQuery,
  })
}

export const useGetLeaderboard = (enabled) => {
  return useReactQuery({
    queryFn: getFamilyGroupsWithPoints,
    staleTime: 0,
    queryKey: qk.points(),
    enabled,
  })
}

export const useGetGroup = (groupId) => {
  return useReactQuery({
    queryFn: () => getGroup(groupId),
    staleTime: 0,
    queryKey: qk.group(groupId),
    enabled: !!groupId,
  })
}

export const useGetInvitations = (enabled) => {
  return useReactQuery({
    queryFn: getInvitationsReceived,
    staleTime: 0,
    queryKey: qk.invitations(),
    enabled,
  })
}

export const useGetFilteringGroups = (enabled) => {
  return useReactQuery({
    queryFn: getFilters,
    staleTime: 86400000, // 1 day,
    queryKey: qk.filteringGroups(),
    enabled,
  })
}

export const useGetUserPoints = (enabled) => {
  return useReactQuery({
    queryFn: getMyPoints,
    staleTime: 0,
    queryKey: qk.myPoints(),
    enabled,
  })
}

export const useGetActivityGroupAnswerById = ({
  activityGroupId,
  activityGroupAnswerId,
  cisUserId,
  enabled = true,
}) => {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const routeMatch = useRouteMatch(activityGroupAllowedRoutes)

  const {
    query: { isError, refetch: getTheAnswers, isFetching: loading },
    data,
  } = useReactQuery({
    queryFn: () =>
      getActivityGroupAnswerById({
        queryKey: ['answer-by-id', { activityGroupId, activityGroupAnswerId, cisUserId }],
      }),
    staleTime: 0,
    queryKey: qk.answerById({ activityGroupId, activityGroupAnswerId, cisUserId }),
    enabled: enabled && !!activityGroupId && !!activityGroupAnswerId,
    refetchOnMount: false,
  })

  useEffect(() => {
    // if fetch fails but answerId still in route or state, explain that answer is unavailable
    if (isError && (routeMatch?.url || activityGroupAnswerId)) {
      showStatusOverlay({
        message: t('error.noAnswer', 'Answer unavailable. It may have been deleted or unshared.'),
        type: 'error',
      })
      setTimeout(() => {
        // Using window.location to hard reset since useHistory doesn't refetch the answers; requires change in feed-posts to close overlay on ERROR
        window.location = `${appPath}${routeMatch.url}`
      }, 3000)
    }
  }, [routeMatch, isError, showStatusOverlay, t, activityGroupAnswerId])

  return [getTheAnswers, { data, loading, isError }]
}

export const useGetPreviousAnswerGroups = (relevantActivityGroupId, personId, enabled) => {
  return useReactQuery({
    queryFn: () => getActivityGroupAnswers({ activityGroupId: relevantActivityGroupId, forPersonId: personId }),
    staleTime: 0,
    queryKey: qk.previousAnswerGroups(relevantActivityGroupId),
    enabled,
  })
}

export const useGetSecondaryPreviousAnswerGroups = (relevantActivityGroupId, enabled) => {
  return useReactQuery({
    queryFn: () => getActivityGroupAnswers({ activityGroupId: relevantActivityGroupId }),
    staleTime: 0,
    queryKey: qk.secondaryPreviousAnswerGroups(relevantActivityGroupId),
    enabled: !!relevantActivityGroupId && enabled,
  })
}

export const useGetActivities = (relevantActivityGroupId, enabled, options = {}) => {
  const { select } = options
  return useReactQuery({
    queryFn: () => getActivities(relevantActivityGroupId),
    staleTime: 86400000, // Cache for a day - these aren't going to change very often at all
    queryKey: qk.activities(relevantActivityGroupId),
    enabled,
    select,
  })
}

export const useGetActivityGroups = (activityGroupId, enabled) => {
  const url = useGetActivityGroupEnabledUrl(`${orchestrationBase}/activity-groups/${activityGroupId}/activity-groups`)
  return useReactQuery({
    queryFn: () => axios.get(url),
    staleTime: 86400000, // Cache for a day - these aren't going to change very often at all
    queryKey: qk.activityGroups(activityGroupId),
    enabled,
  })
}

export const useGetActivityGroup = ({ activityGroupId, enabled, select }) => {
  const url = useGetActivityGroupEnabledUrl(`${orchestrationBase}/activity-groups/${activityGroupId}`)
  return useReactQuery({
    queryFn: () => axios.get(url),
    staleTime: 86400000,
    queryKey: qk.activityGroup(activityGroupId),
    enabled,
    select,
  })
}

export const useGetFamilyGroups = (includePendingGroups) => {
  return useReactQuery({
    queryFn: () => getFamilyGroups(includePendingGroups),
    staleTime: 0,
    queryKey: qk.familyGroups(includePendingGroups),
  })
}

export const useGetFamilyGroupMembers = (familyGroupId) => {
  return useReactQuery({
    queryFn: () => getGroupMembers(familyGroupId),
    staleTime: 10000,
    queryKey: qk.familyGroupMembers(familyGroupId),
  })
}

export const useSaveAnswers = (activityGroupIdForKey) => {
  const queryClient = useQueryClient()
  const { cisId: activeCisId } = useUser()

  // Helper functions extracted from onMutate to reduce cognitive complexity
  const applySelectionUpdate = (current, first) => {
    const selections = current?.selections ?? []
    const incoming = first?.selections ?? []
    let updated = selections
    incoming.forEach((sel) => {
      updated = updateAnswerInArray(updated, sel)
    })
    return { ...current, selections: updated }
  }

  const applyRankingUpdate = (current, first) => {
    const existing = current?.rankings ?? current?.ranking ?? []
    let rankings
    if (first?.delete) {
      const toRemoveId = first?.id || first?.activityId
      rankings = existing.filter((r) => (first?.id ? r?.id !== toRemoveId : r?.activityId !== toRemoveId))
    } else {
      rankings = first?.rankings ?? []
    }
    const { ranking, ...rest } = current || {}
    return { ...rest, rankings }
  }

  const applyFormFieldUpdate = (current, answers) => {
    let formFields = current?.formFields ?? []
    answers.forEach((ff) => {
      formFields = updateAnswerInArray(formFields, ff)
    })
    return { ...current, formFields }
  }

  const applyQuestionAnswerUpdate = (current, first) => {
    const conversations = current?.conversations ? [...current.conversations] : []
    const matchIndex = conversations.findIndex((c) =>
      first?.id ? c?.id === first?.id : c?.activityId === first?.activityId
    )
    if (first?.delete) {
      if (matchIndex !== -1) conversations.splice(matchIndex, 1)
    } else {
      const normalized = { ...first, title: first?.title ?? '' }
      if (matchIndex !== -1) conversations[matchIndex] = { ...conversations[matchIndex], ...normalized }
      else conversations.push(normalized)
    }
    return { ...current, conversations }
  }

  const applyAnswerTypeUpdates = ({ current, answers }) => {
    if (!Array.isArray(answers) || answers.length === 0) return current
    const [first] = answers
    switch (first?.type) {
      case 'selection':
        return applySelectionUpdate(current, first)
      case 'ranking':
        return applyRankingUpdate(current, first)
      case 'form-field':
        return applyFormFieldUpdate(current, answers)
      case 'question-answer':
      case 'question-answer-photos':
        return applyQuestionAnswerUpdate(current, first)
      default:
        return current
    }
  }

  return useReactQueryMutation({
    mutationFn: saveAnswers,
    mutationKey: ['save-answers', activityGroupIdForKey],
    onMutate: async (variables) => {
      const { activityGroupId, activityGroupAnswerId, data } = variables || {}
      const cisUserId = activeCisId
      const key = qk.answerById({ activityGroupId, activityGroupAnswerId, cisUserId })

      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      const prevValue = previous?.data // axios response shape { data: ... }
      let current = { ...(prevValue || {}) }
      if (data?.title) current = { ...current, title: data.title }
      if (!current?.id && activityGroupAnswerId) current = { ...current, id: activityGroupAnswerId }
      current = applyAnswerTypeUpdates({ current, answers: data?.answers })

      queryClient.setQueryData(key, { ...(previous || {}), data: current })

      return { key, previous }
    },
    //  Roll back the update if the mutation fails
    onError: (_err, _vars, context) => {
      if (context?.key) queryClient.setQueryData(context.key, context.previous)
    },
    onSuccess: (response, variables, context) => {
      const clientFirst = variables?.data?.answers?.[0]
      if (clientFirst?.type && response?.data?.answers?.[0]) {
        // Clone to avoid mutating server response
        const clonedResponse = {
          ...response,
          data: {
            ...response.data,
            answers: response.data.answers.map((answer, index) =>
              index === 0 ? { ...answer, type: clientFirst.type } : answer
            ),
          },
        }
        if (context?.key) queryClient.setQueryData(context.key, clonedResponse)
      } else if (context?.key) {
        queryClient.setQueryData(context.key, response)
      }
      const agid = variables?.activityGroupId || activityGroupIdForKey
      invalidatePreviousAnswerGroups(queryClient, agid)
      invalidatePoints(queryClient)
    },
  })
}

export const useSaveMemoryDescription = () => {
  return useReactQueryMutation({
    mutationFn: saveMemoryDescription,
    mutationKey: ['save-memory-description'],
  })
}

export const useGetMemoryFromMemories = (artifactId) => {
  return useReactQuery({
    queryFn: () => getMemoryFromMemories({ artifactId }),
    staleTime: 0,
    queryKey: qk.memoryFromMemories(artifactId),
    enabled: !!artifactId,
  })
}

export const useSaveMemoryTitle = () => {
  return useReactQueryMutation({
    mutationFn: saveMemoryTitle,
    mutationKey: ['save-memory-description'],
  })
}

export const useUpdateMemoryDate = () => {
  return useReactQueryMutation({
    mutationFn: updateMemoryDate,
    mutationKey: ['update-memory-date'],
  })
}

export const useSaveImageToMemory = () => {
  return useReactQueryMutation({
    mutationFn: addImagesToMemory,
    mutationKey: ['save-images-tomemory'],
  })
}

export const useRemoveImageFromMemory = () => {
  return useReactQueryMutation({
    mutationFn: removeImageFromMemory,
    mutationKey: ['remove-image-from-memory'],
  })
}

export const useMarkAnswerAsRead = (activity) => {
  const { activityGroupAnswerId, memoryArtifact } = activity
  const user = useUser()
  const activityIsCompleted = activity?.activityGroupAnswers?.completeDate || memoryArtifact?.completeDate
  const activityCisId = activity?.activityGroupAnswers?.cisUserId || memoryArtifact?.authorCisId
  useEffect(() => {
    if (activity && activityIsCompleted && user?.cisId) {
      const userIsAuthor = user.cisId === activityCisId
      if (!userIsAuthor) markAnswerAsRead(activityGroupAnswerId)
    }
  }, [activityIsCompleted, activityGroupAnswerId, user.cisId, activityCisId, activity])
}

export const useGetCampaigns = ({ enabled, isMember }) => {
  return useReactQuery({
    queryFn: () => getCampaigns(isMember),
    staleTime: 86400000,
    queryKey: qk.campaigns(),
    enabled,
  })
}

export const useGetPhotosForAlbum = (activityGroupId) => {
  return useReactQuery({
    queryFn: () => getPhotosForAlbum(activityGroupId),
    staleTime: 0,
    queryKey: qk.photosForAlbum(activityGroupId),
  })
}

const mapArtifacts = ({ artifacts, entries, sessionId }) => {
  return artifacts.map((artifactResponse) => {
    if (!artifactResponse) return null
    return {
      id: entries.find(({ memoryArtifactId }) => memoryArtifactId === artifactResponse.data.id)?.id,
      memoryArtifactId: artifactResponse.data.id,
      title: artifactResponse.data.title,
      thumbMobileUrl: `${artifactResponse.data.thumbMobileUrl}&sessionId=${sessionId}`,
      src: `${artifactResponse.data.url}&sessionId=${sessionId}`,
      alt: '',
      artifactDisplayState: artifactResponse?.data?.artifactDisplayState,
    }
  })
}

export const useGetPhotoAlbum = (activityGroupId) => {
  const [photos, setPhotos] = useState([])
  const sessionId = getSessionId()
  const { query, data } = useGetPhotosForAlbum(activityGroupId)

  const [arePhotoRequestsInProgress, setArePhotoRequestsInProgress] = useState(false)

  useEffect(() => {
    if (data) {
      const { entries = [] } = data
      const artifactCollection = entries.map(({ memoryArtifactId }) => {
        return getMemoryById({ memoryArtifactId }).catch((err) => {
          throw err
        })
      })
      const collectArtifacts = () => Promise.all(artifactCollection)
      setArePhotoRequestsInProgress(true)
      collectArtifacts().then((artifacts) => {
        setArePhotoRequestsInProgress(false)
        let allPhotos = mapArtifacts({ artifacts, entries, sessionId })
        allPhotos = allPhotos.filter((val) => val !== null) // Remove any duds
        setPhotos(allPhotos)
      })
    }
  }, [data, sessionId])

  const refreshPhotos = () => {
    query.refetch()
  }
  return [refreshPhotos, photos, query.isLoading || arePhotoRequestsInProgress]
}

export const useSavePhotoForAlbum = () => {
  const queryClient = useQueryClient()
  return useReactQueryMutation({
    mutationFn: (inputData) => {
      const {
        answerOrAnswers: { entries },
      } = inputData
      const { activityGroupId, parentVisibleId, activityId, memoryArtifactId } = (entries && entries[0]) || {}
      const postPayload = {
        entries: [
          {
            parentVisibleId,
            activityId,
            memoryArtifactId,
          },
        ],
      }
      return savePhotoForAlbum(postPayload, activityGroupId)
    },
    mutationKey: ['save-photo-for-album'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.activityGroupAnswerCount() })
    },
  })
}

export const useDeletePhotoFromAlbum = () => {
  const queryClient = useQueryClient()
  return useReactQueryMutation({
    mutationFn: deletePhotoFromAlbum,
    mutationKey: ['delete-photo-from-album'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.activityGroupAnswerCount() })
    },
  })
}

export const useMarkAsComplete = () => {
  const queryClient = useQueryClient()
  return useReactQueryMutation({
    mutationFn: markAsComplete,
    mutationKey: ['mark-complete'],
    onSuccess: () => {
      invalidatePoints(queryClient)
    },
  })
}

export const useSetGrouping = () => {
  return useReactQueryMutation({
    mutationFn: setGrouping,
    mutationKey: ['set-grouping'],
  })
}

export const useDeleteAnswerGroup = () => {
  const queryClient = useQueryClient()
  return useReactQueryMutation({
    mutationFn: deleteAnswerGroup,
    mutationKey: ['delete-answer-group'],
    onMutate: async (variables) => {
      const { activityGroupId } = variables || {}
      const key = qk.previousAnswerGroups(activityGroupId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      const next = { ...(previous || {}) }
      if (next?.data?.answers && variables?.activityGroupAnswerId) {
        next.data.answers = next.data.answers.filter((a) => a?.id !== variables.activityGroupAnswerId)
      }
      queryClient.setQueryData(key, next)
      return { key, previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.key) queryClient.setQueryData(context.key, context.previous)
    },
    onSuccess: (_resp, variables) => {
      const agid = variables?.activityGroupId
      invalidatePreviousAnswerGroups(queryClient, agid)
    },
  })
}

export const useGetStreaks = (enabled) => {
  return useReactQuery({
    queryFn: getStreaks,
    staleTime: 300000,
    queryKey: qk.streaks(),
    enabled,
  })
}

// Given selected activities (with saved answers), fetch their images in parallel
export const useSelectedActivityImages = ({ items, activityId = null } = {}) => {
  const sessionId = getSessionId()

  const targetItems = Array.isArray(items) ? items : []
  const filtered = activityId ? targetItems.filter((it) => it?.savedAnswer?.activityId === activityId) : targetItems

  const ids = filtered.flatMap((it) => it?.savedAnswer?.memoryArtifactIds || [])

  const queries = ids.map((id) => ({
    queryKey: qk.memory(id),
    queryFn: () => getMemoryById({ memoryArtifactId: id }),
    staleTime: 0,
    enabled: true,
  }))

  const results = useQueries({ queries })

  const images = results
    .filter((q) => q?.isSuccess && q?.data?.data)
    .map((q) => {
      const d = q.data.data
      return {
        id: d.id,
        thumbMobileUrl: d.thumbMobileUrl ? `${d.thumbMobileUrl}&sessionId=${sessionId}` : undefined,
        url: d.url ? `${d.url}&sessionId=${sessionId}` : undefined,
        title: d.title,
        artifactDisplayState: d.artifactDisplayState,
      }
    })

  const isLoading = results.some((q) => q?.isLoading)
  const hasErrors = results.some((q) => q?.isError)
  const isSuccessCount = results.filter((q) => q?.isSuccess).length

  const isPartialSuccess = isSuccessCount > 0 && hasErrors
  const isComplete = results.length > 0 && !isLoading && !isPartialSuccess && !hasErrors

  const errors = results.filter((q) => q?.isError).map((q) => ({ error: q.error }))

  const refetch = () => {
    results.forEach((q) => q?.refetch?.())
  }

  if (!items || ids.length === 0) {
    return {
      images: [],
      isLoading: false,
      isPartialSuccess: false,
      isComplete: true,
      hasErrors: false,
      errors: [],
      refetch: () => {},
    }
  }

  return { images, isLoading, isPartialSuccess, isComplete, hasErrors, errors, refetch }
}

export const usePostPushNotificationRegistration = () => {
  return useReactQueryMutation({
    mutationFn: postPushNotificationRegistration,
    mutationKey: ['post-push-notification-registration'],
  })
}

// Saved sharing preferences for a specific answer
export const useGetSavedSharingPreferences = ({ activityGroupAnswerId, enabled = true }) => {
  return useReactQuery({
    queryFn: () => getSharingPreferences(activityGroupAnswerId),
    staleTime: 0,
    queryKey: qk.sharingPreference(activityGroupAnswerId),
    enabled: !!activityGroupAnswerId && enabled,
  })
}

// Delete a saved sharing preference for a family group (optimistic)
export const useDeleteSavedSharingPreference = () => {
  const queryClient = useQueryClient()
  return useReactQueryMutation({
    mutationFn: deleteSharingPreference,
    mutationKey: ['delete-saved-sharing-preference'],
    onMutate: async (variables) => {
      const { activityGroupAnswerId, familyGroupId } = variables || {}
      const key = qk.sharingPreference(activityGroupAnswerId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      const next = { ...(previous || {}) }
      if (next?.data?.items && familyGroupId) {
        next.data.items = next.data.items.filter((it) => it?.familyGroupId !== familyGroupId)
      }
      queryClient.setQueryData(key, next)
      return { key, previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.key) queryClient.setQueryData(context.key, context.previous)
    },
    onSuccess: (_resp, variables) => {
      const key = qk.sharingPreference(variables?.activityGroupAnswerId)
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

// Save sharing (add/remove) for an answer across groups
// Returns an array with a single function for historical compatibility: [saveSharing]
export const useSaveSharing = () => {
  const queryClient = useQueryClient()
  const saveSharing = async ({
    groupIdsToShareWith = [],
    groupsToDeleteSharing = [],
    activityGroupId,
    activityGroupAnswerId,
    file,
  }) => {
    if (!activityGroupId || !activityGroupAnswerId) return

    // Create POST calls for each groupId to add sharing (with optional image/file)
    const callsToAdd = groupIdsToShareWith.map((groupId) => {
      const formData = new FormData()
      if (file) formData.append('file', file)
      return (
        groupId &&
        axios.post(
          `${orchestrationBase}/activity-groups/answers/${activityGroupAnswerId}/sharings/${groupId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )
      )
    })

    // Create DELETE calls for removals
    const callsToDelete = groupsToDeleteSharing.map((groupDetail) => {
      const familyGroupId = groupDetail?.[0] || ''
      return familyGroupId && deleteSharingPreference({ activityGroupAnswerId, familyGroupId })
    })

    // Execute all
    await Promise.allSettled([...callsToAdd, ...callsToDelete])

    // Invalidate cache so UI can reflect changes
    queryClient.invalidateQueries({ queryKey: qk.sharingPreference(activityGroupAnswerId) })
    queryClient.invalidateQueries({ queryKey: qk.previousAnswerGroups(activityGroupId) })
  }

  return [saveSharing]
}

// Consolidated answers for Story page
export const useGetConsolidatedAnswers = () => {
  return useReactQuery({
    queryFn: getConsolidatedAnswers,
    staleTime: 0,
    queryKey: qk.consolidatedAnswers(),
    enabled: true,
  })
}

// Temple eligibility
export const useGetTempleEligible = (enabled) => {
  return useReactQuery({
    queryFn: getTempleEligible,
    staleTime: 300000,
    queryKey: qk.templeEligible(),
    enabled,
  })
}

export const useGetChildActivityGroupsAnswers = (childActivityGroups, personId) => {
  // Create stable array of activity group IDs for query key
  const activityGroupIds = useMemo(
    () => childActivityGroups?.map((ag) => ag.visibleId).filter(Boolean) || [],
    [childActivityGroups]
  )

  // Create a query for each activity group ID
  const queries = useQueries({
    queries: activityGroupIds.map((activityGroupId) => ({
      // Shared key enables broad invalidation after save/delete so Completed list updates.
      queryKey: qk.activityGroupAnswers(activityGroupId, personId),
      queryFn: () =>
        getActivityGroupAnswers({
          activityGroupId,
          forPersonId: personId,
        }),
      enabled: !!activityGroupId,
      staleTime: 300000, // 5 minutes
      retry: 1, // Retry failed requests once before giving up
    })),
  })

  // Track loading and error states
  const someQueriesLoading = queries.some((query) => query.isLoading)
  const someQueriesFailed = queries.some((query) => query.isError)

  // Process available results, including partial successes
  const allAnswers = queries
    .filter((query) => query.isSuccess && query.data?.data?.answers) // Only include successful queries with answers
    .reduce((acc, { data }) => {
      if (data?.data?.answers) {
        acc.push(...data.data.answers)
      }
      return acc
    }, [])

  // Note: Debug logging temporarily disabled for production

  // Collect any errors for the consumer to handle if needed
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => ({
      activityGroupId: query.queryKey[1], // Extract activity group ID from query key
      error: query.error,
    }))

  return {
    data: allAnswers,
    isLoading: someQueriesLoading && activityGroupIds.length > 0,
    isPartialSuccess: allAnswers.length > 0 && someQueriesFailed && !someQueriesLoading,
    isComplete: queries.length > 0 && queries.every((query) => query.isSuccess),
    hasErrors: someQueriesFailed,
    errors,
    error: someQueriesFailed ? errors[0]?.error : null, // Maintain compatibility with existing error handling
    refetch: () => queries.forEach((query) => query.refetch()),
  }
}

export const useGetActivityGroupImages = (enabled = true) => {
  return useReactQuery({
    queryFn: getActivityGroupImages,
    staleTime: 86400000, // 1 day - icons don't change often
    queryKey: ['activity-group-icons'],
    enabled,
    select: (response) => {
      const items = response?.data?.items

      if (!Array.isArray(items)) {
        return {}
      }

      return items.reduce((acc, image) => {
        if (
          image &&
          typeof image === 'object' &&
          image.visibleId !== undefined &&
          image.shape !== undefined &&
          image.size !== undefined
        ) {
          const { visibleId, shape, size } = image

          if (!acc[visibleId]) {
            acc[visibleId] = {
              all: [],
              byShape: {},
            }
          }

          acc[visibleId].all.push(image)

          if (!acc[visibleId].byShape[shape]) {
            acc[visibleId].byShape[shape] = {}
          }

          if (!acc[visibleId].byShape[shape][size]) {
            acc[visibleId].byShape[shape][size] = []
          }
          acc[visibleId].byShape[shape][size].push(image)
        }
        return acc
      }, {})
    },
  })
}
